import { IMAGES } from "@/lib/constants";
import { Overline, GhostButton } from "@/components/site/Primitives";
import { NewsletterSection } from "@/components/site/Newsletter";
import { Reveal } from "@/lib/reveal";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative h-[60vh]">
        <img src={IMAGES.hero} alt="Rosewood Cottage" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-charcoal/40" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-10 w-full pb-16">
            <Overline className="text-brand-cream">The keeper of Rosewood Cottage</Overline>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-brand-cream mt-4 leading-none">About Agnes</h1>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-24 md:py-32">
        <Reveal>
          <p className="font-serif text-3xl font-light leading-snug text-brand-charcoal mb-10">
            I am Agnes Whitmore. For seventy-eight years I have lived a small and beautiful life — and I should like to share it with you.
          </p>
          <div className="font-body text-brand-body text-lg leading-loose space-y-6">
            <p>I was born in a stone cottage at the edge of a Cotswold village, the youngest of five. My mother kept a garden that fed us and a pantry that never emptied, and my grandmother knew a remedy for every small ailment that ever troubled a child. I learned at their elbows — not from books, but from watching hands that had done these things ten thousand times.</p>
            <p>When I married and came to Rosewood Cottage, I brought those ways with me. I baked our bread each week, dried herbs from the hedgerows, preserved the orchard's gift each autumn, and kept a home that held us gently through every season. It was not a grand life. It was a full one.</p>
            <p className="font-serif text-2xl italic text-brand-olive">"The old ways were never about doing more. They were about doing things with love."</p>
            <p>Somewhere in these last decades, I began to fear that all of it — the recipes never written down, the remedies passed only by word of mouth, the quiet arts of a well-kept home — would be lost. And so I began to write. Season by season, page by page, I have gathered everything I know.</p>
            <p>This is not a business to me. It is a inheritance I wish to leave. Whether you have found your way here from a busy city or a quiet lane, I hope you will feel, as you wander these rooms, that you have stepped out of the noise and into a warm kitchen — where the kettle is always on, and there is always time.</p>
            <p className="font-serif text-2xl text-brand-charcoal">With warmth,<br/>Agnes</p>
          </div>
        </Reveal>
        <div className="mt-14"><GhostButton to="/library" testid="about-library-btn">Explore the Cottage Library</GhostButton></div>
      </section>

      <section className="bg-brand-warm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-16 items-center">
          <Reveal><img src={IMAGES.agnes} alt="Agnes at her table" className="w-full aspect-[4/5] object-cover" /></Reveal>
          <Reveal delay={0.1}>
            <Overline>What we believe</Overline>
            <h2 className="text-4xl sm:text-5xl font-light mt-4 mb-8 leading-tight">A slower, softer way of living</h2>
            {[
              ["Warmth over trend", "We follow no fashions. Only what is timeless, comforting and true."],
              ["Craft over convenience", "Everything here is made slowly, by hand, and with great care."],
              ["Wisdom over noise", "We preserve the knowledge that once passed quietly between generations."],
            ].map(([t, d]) => (
              <div key={t} className="border-b border-brand-linen py-6">
                <h3 className="font-serif text-2xl mb-2">{t}</h3>
                <p className="font-body text-brand-body leading-relaxed">{d}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
      <NewsletterSection />
    </div>
  );
}
